import {
  PORTAL_CLIENT_FIELDS,
  UNIT_LABELS,
  canonicalJson,
  contractContent,
  formatBytes,
  formatEuros,
  legalVars,
  renderLegalText,
  sha256Hex,
  utf8,
  uuidv7,
  type PortalView,
  type Strokes,
} from '@clim/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SignaturePad } from '../components/SignaturePad';
import { ErrorBox, Field, Input } from '../components/ui';
import { isAllowedMime, preparePhoto, readDocument } from '../lib/images';
import { portalApi } from '../lib/portal-api';

const FIELD_LABEL: Record<(typeof PORTAL_CLIENT_FIELDS)[number], string> = {
  email: 'E-mail',
  phone: 'Téléphone',
  addressLine1: 'Adresse',
  addressLine2: 'Complément d’adresse',
  postalCode: 'Code postal',
  city: 'Commune',
};

/**
 * Portail client (CDC §5) : sans compte, depuis le lien reçu par SMS / WhatsApp / e-mail.
 * Le secret est dans le FRAGMENT de l'URL (#…), jamais transmis aux serveurs web ; il est
 * retiré de la barre d'adresse et conservé pour la session de l'onglet uniquement.
 */
function readToken(): string {
  const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
  try {
    if (fromHash) {
      sessionStorage.setItem('clim-portal', fromHash);
      history.replaceState(null, '', window.location.pathname);
      return fromHash;
    }
    return sessionStorage.getItem('clim-portal') ?? '';
  } catch {
    return fromHash;
  }
}

export function PortalPage() {
  const [token] = useState(readToken);
  const api = useMemo(() => portalApi(token), [token]);
  const [view, setView] = useState<PortalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => api.view().then(setView, (e) => setError(e.message)), [api]);
  useEffect(() => {
    void reload();
  }, [reload]);

  if (error && !view) {
    return (
      <main className="portal">
        <h1>Lien indisponible</h1>
        <ErrorBox message={error} />
      </main>
    );
  }
  if (!view) return <div className="splash">Chargement…</div>;
  const d = view.dossier;
  const addr = [d.siteAddressLine1, d.siteAddressLine2, [d.sitePostalCode, d.siteCity].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const preview = contractContent({
    company: view.company.legal,
    dossier: d,
    client: view.client,
    lines: view.lines,
    texts: { terms: [], consents: [], privacy: null, withdrawal: null, reserves: view.reserveTexts },
    acceptedConsentIds: [],
    signer: { name: '', quality: 'client' },
    signedAt: new Date().toISOString(),
  });
  const total = preview.totals;

  return (
    <main className="portal" style={view.company.primaryColor ? ({ '--blue': view.company.primaryColor } as React.CSSProperties) : undefined}>
      <header className="portal-head">
        {view.company.logoUrl && <img src={view.company.logoUrl} alt={view.company.name} className="portal-logo" />}
        <div>
          <h1>{d.typeLabel}</h1>
          <p className="muted">
            {view.company.name}
            {d.reference && ` · dossier ${d.reference}`} · lien valable jusqu’au {new Date(view.link.expiresAt).toLocaleDateString('fr-FR')}
          </p>
        </div>
      </header>

      <section className="card">
        <h2>Votre commande</h2>
        {view.lines.length === 0 ? (
          <p className="muted">Votre conseiller n’a pas encore ajouté les produits.</p>
        ) : (
          <table className="table">
            <tbody>
              {view.lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.label}
                    {l.model && <span className="muted"> · {l.model}</span>}
                  </td>
                  <td className="nowrap">
                    {l.quantity} {UNIT_LABELS[l.unit]}
                  </td>
                  <td className="nowrap num">{formatEuros(Math.round(l.quantity * l.unitPriceHtCents))} HT</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view.lines.length > 0 && (
          <p className="num">
            Total TTC <strong>{formatEuros(total.ttcCents)}</strong> <span className="muted">(dont TVA {formatEuros(total.vatCents)})</span>
          </p>
        )}
        {addr && <p>Adresse d’intervention : {addr}</p>}
        {preview.reserves.map((r, i) => (
          <div key={i} className="reserve-preview">
            <strong>{r.title}</strong>
            <div className="pre">{r.body}</div>
          </div>
        ))}
        <p className="muted">
          {preview.sale.withdrawal.applies
            ? `Vous disposez d’un droit de rétractation de ${preview.sale.withdrawal.days} jours ; le formulaire sera joint à votre bon de commande.`
            : 'Ce contrat n’ouvre pas de droit de rétractation.'}
        </p>
      </section>

      {view.link.allowEdit && !d.signature && <ContactForm view={view} api={api} onSaved={reload} />}
      {view.link.allowUpload && <Documents view={view} api={api} onChanged={reload} />}
      <SignSection view={view} api={api} onSigned={reload} />
      {view.payment && <PaymentSection payment={view.payment} />}
      <p className="muted small">Vos données sont traitées par {view.company.name} pour votre commande. Ce lien est personnel : ne le transférez pas.</p>
    </main>
  );
}

type Api = ReturnType<typeof portalApi>;

function PaymentSection({ payment }: { payment: NonNullable<PortalView['payment']> }) {
  return (
    <section className="card">
      <h2>Règlement</h2>
      <dl className="money">
        <div>
          <dt>Total TTC</dt>
          <dd>{formatEuros(payment.totalCents)}</dd>
        </div>
        <div>
          <dt>Déjà réglé</dt>
          <dd>{formatEuros(payment.paidCents)}</dd>
        </div>
        <div className="balance">
          <dt>Reste à régler</dt>
          <dd>{formatEuros(payment.balanceCents)}</dd>
        </div>
      </dl>
      {payment.links.map((l) => (
        <p key={l.url}>
          <a className="btn primary" href={l.url}>
            💳 Payer {l.isDeposit ? 'l’acompte de ' : ''}
            {formatEuros(l.amountCents)} par carte
          </a>{' '}
          <span className="muted">Paiement sécurisé Stripe · lien valable jusqu’au {new Date(l.expiresAt).toLocaleString('fr-FR')}</span>
        </p>
      ))}
    </section>
  );
}

function ContactForm({ view, api, onSaved }: { view: PortalView; api: Api; onSaved(): void }) {
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(PORTAL_CLIENT_FIELDS.map((f) => [f, (view.client[f] as string | null) ?? ''])));
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="card">
      <h2>Vos coordonnées</h2>
      <p>
        <strong>{view.client.companyName ?? [view.client.firstName, view.client.lastName].filter(Boolean).join(' ')}</strong>
      </p>
      <div className="form grid2">
        {PORTAL_CLIENT_FIELDS.map((f) => (
          <Field key={f} label={FIELD_LABEL[f]}>
            <Input value={v[f]} onChange={(e) => setV({ ...v, [f]: e.target.value })} type={f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'} />
          </Field>
        ))}
      </div>
      <ErrorBox message={error} />
      {msg && <p role="status">{msg}</p>}
      <button
        className="btn primary"
        onClick={() => {
          setError(null);
          setMsg(null);
          api
            .updateClient(Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim() || null])))
            .then(() => {
              setMsg('Coordonnées enregistrées. Merci !');
              onSaved();
            }, (e) => setError(e.message));
        }}
      >
        Enregistrer mes coordonnées
      </button>
    </section>
  );
}

function Documents({ view, api, onChanged }: { view: PortalView; api: Api; onChanged(): void }) {
  const [name, setName] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = async (files: FileList | null) => {
    setError(null);
    for (const file of [...(files ?? [])]) {
      try {
        const isImage = file.type.startsWith('image/');
        const doc = isImage ? await preparePhoto(file) : await readDocument(file);
        if (!isAllowedMime(doc.mime)) throw new Error(`« ${file.name} » : format non accepté (PDF ou photo)`);
        setProgress(0);
        await api.upload(view.dossier.id, { kind: isImage ? 'photo' : 'document', name: name.trim() || file.name, mime: doc.mime, data: doc.data, originalFilename: file.name }, setProgress);
      } catch (e) {
        setError((e as Error).message);
      }
    }
    setProgress(null);
    setName('');
    onChanged();
  };
  return (
    <section className="card">
      <h2>Vos documents</h2>
      <p className="muted">Pièce d’identité, justificatif, KBIS, photos… Vous pouvez en ajouter plusieurs.</p>
      {view.files.length > 0 && (
        <ul className="plain files">
          {view.files.map((f) => (
            <li key={f.id}>
              <div className="row-main">
                <strong>{f.name}</strong>
                <span className="muted">
                  {formatBytes(f.sizeBytes)} · {f.status === 'ready' ? 'reçu' : 'envoi incomplet'}
                </span>
              </div>
              {f.status === 'ready' && (
                <button className="btn small" onClick={() => void api.fileUrl(f.id).then((u) => window.open(u, '_blank', 'noopener'), (e) => setError(e.message))}>
                  Ouvrir
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="form inline-form">
        <Field label="Nom du document (facultatif)">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Pièce d’identité" />
        </Field>
        <label className="btn primary file-btn">
          📎 Ajouter un document ou une photo
          <input type="file" accept="application/pdf,image/*" multiple onChange={(e) => void send(e.target.files).then(() => (e.target.value = ''))} />
        </label>
      </div>
      {progress !== null && <p role="status">Envoi… {progress} %</p>}
      <ErrorBox message={error} />
    </section>
  );
}

function SignSection({ view, api, onSigned }: { view: PortalView; api: Api; onSigned(): void }) {
  const d = view.dossier;
  // cases obligatoires déjà cochées (le client décoche si besoin) ; la case facultative (offres commerciales) reste à cocher par le client
  const [consentIds, setConsentIds] = useState<string[]>(() => view.consents.filter((c) => c.required).map((c) => c.id));
  const [signerName, setSignerName] = useState(view.client.companyName ?? [view.client.firstName, view.client.lastName].filter(Boolean).join(' '));
  const [strokes, setStrokes] = useState<Strokes>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (d.signature) {
    return (
      <section className="card signed-box">
        <h2>Commande signée</h2>
        <p>
          ✅ Signée par {d.signature.signerName} le {new Date(d.signature.signedAt).toLocaleString('fr-FR')}. Votre bon de commande signé figure dans vos documents.
        </p>
      </section>
    );
  }
  if (!view.link.allowSign) return null;
  if (!d.signable) {
    return (
      <section className="card">
        <h2>Signature</h2>
        <p className="muted">Votre dossier n’est pas encore prêt à être signé. Votre conseiller vous préviendra.</p>
      </section>
    );
  }
  const missing = view.consents.filter((c) => c.required && !consentIds.includes(c.id));
  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      const signedAt = new Date().toISOString();
      const content = contractContent({
        company: view.company.legal,
        dossier: d,
        client: view.client,
        lines: view.lines,
        texts: { terms: view.terms, consents: view.consents, privacy: view.privacy, withdrawal: view.withdrawalForm, reserves: view.reserveTexts },
        acceptedConsentIds: consentIds,
        signer: { name: signerName, quality: 'client' },
        signedAt,
      });
      await api.sign({
        signatureId: uuidv7(),
        signerName: signerName.trim(),
        signerQuality: 'client',
        termIds: view.terms.map((t) => t.id),
        presentedConsentIds: view.consents.map((t) => t.id),
        privacyId: view.privacy?.id ?? null,
        withdrawalFormId: view.withdrawalForm?.id ?? null,
        consentIds,
        strokes,
        contentHash: await sha256Hex(utf8(canonicalJson(content))),
        signedAt,
      });
      onSigned();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <h2>Signer ma commande</h2>
      {view.terms.map((t) => (
        <details key={t.id}>
          <summary>
            {t.title} (version {t.textVersion})
          </summary>
          <div className="pre small terms">{renderLegalText(t.body, legalVars(view.company.legal))}</div>
        </details>
      ))}
      {view.privacy && (
        <details>
          <summary>{view.privacy.title}</summary>
          <div className="pre small terms">{renderLegalText(view.privacy.body, legalVars(view.company.legal))}</div>
        </details>
      )}
      <div className="form">
        {view.consents.map((t) => (
          <label key={t.id} className="check consent">
            <input type="checkbox" checked={consentIds.includes(t.id)} onChange={(e) => setConsentIds(e.target.checked ? [...consentIds, t.id] : consentIds.filter((x) => x !== t.id))} />
            <span>
              <strong>
                {t.title}
                {t.required ? ' *' : ' (facultatif)'}
              </strong>{' '}
              — {renderLegalText(t.body, legalVars(view.company.legal))}
            </span>
          </label>
        ))}
        <Field label="Nom du signataire">
          <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} />
        </Field>
        <span className="field-label">Votre signature</span>
        <SignaturePad value={strokes} onChange={setStrokes} />
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={() => setStrokes([])} disabled={!strokes.length}>
            Effacer
          </button>
          <button className="btn primary" disabled={busy || !strokes.length || missing.length > 0 || !signerName.trim()} onClick={() => void sign()}>
            Signer
          </button>
        </div>
        {missing.length > 0 && <p className="muted small">Cochez les mentions obligatoires (*) pour signer.</p>}
      </div>
    </section>
  );
}
